import '@stencil/router';
import 'reflect-metadata';
import '@stencil/redux';
import 'uikit';
import { Component, Prop, ComponentInterface, h, State, Watch, Listen } from '@stencil/core';
import { store } from '@stencil/redux';
import { configureStore } from '../../stores';
import { AuthenticateRoute } from '../authenticated-route/authenticated-route';
import { ApolloClient, createHttpLink, InMemoryCache } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { UserDTO } from '../../dto/user.dto';
import { fetchUser, getSettings } from '../../actions';
import { environment } from '../../environments/environment';
import * as Cookies from 'es-cookie';
import { watch } from 'fs';
import { setToken } from '../../utilities/global';

@Component({
  tag: 'accounts-app',
  styleUrl: 'app.scss'
})
export class App implements ComponentInterface {
  @Prop() client: ApolloClient<any>;

  @Prop() defaultClient: ApolloClient<any>;

  @State() currentUser: UserDTO;

  @State() settings;

  @State() isNewRoute = false;

  getUser!: typeof fetchUser;

  getUserSettings!: typeof getSettings;

  componentWillLoad() {
    // Set the apollo link
    let token: string = window['AUTH_TOKEN'] || Cookies.get('auth_token');
    if (!token) {
      setToken();
      token = Cookies.get('auth_token');
    }
    const authLink = setContext((_, { headers }) => {
      return {
        headers: {
          ...headers,
          authorization: token ? `Bearer ${token}` : ''
        }
      };
    });

    // set the client property
    this.client = new ApolloClient({
      link: authLink.concat(
        createHttpLink({
          uri: environment.gatewayEndpoint
        })
      ),
      cache: new InMemoryCache({ resultCaching: true })
    });

    this.defaultClient = new ApolloClient({
      link: authLink.concat(
        createHttpLink({
          uri: environment.apiEndpoint
        })
      ),
      cache: new InMemoryCache({ resultCaching: true })
    });

    store.setStore(
      configureStore({
        graphClient: this.client,
        defaultGraphClient: this.defaultClient
      })
    );

    store.mapStateToProps(this, (state) => {
      const {
        user: { currentUser, settings }
      } = state;
      return {
        currentUser,
        settings
      };
    });

    store.mapDispatchToProps(this, {
      getUser: fetchUser,
      getUserSettings: getSettings
    });

    if (
      [
        '/ig-authenticate',
        '/instagram/authenticate',
        '/instagram/authorize',
        '/pinterest/authenticate',
        '/pinterest/authorize',
        '/tiktok/authenticate'
      ].indexOf(window.location.pathname) === -1
    ) {
      if (!this.currentUser) {
        this.getUser({ useSCPAuth: this.settings?.enable_new_auth ?? false });
      }

      this.getUserSettings();
    } else {
      this.isNewRoute = true;
    }
  }

  createLinkElement(rel, href, type = null) {
    const link = document.createElement('link');
    link.rel = rel;
    if (type) {
      link.type = type;
    }
    link.href = href;
    document.head.appendChild(link);
  }

  async setOnBoardingFlag() {
    this.isNewRoute = true;
  }

  @Watch('currentUser')
  watchHandler(newValue) {
    if (newValue) {
      this.setOnBoardingFlag();
    }
  }

  @Listen('updateCurrentUser')
  onUpdateCurrentUser(event: CustomEvent) {
    this.currentUser = event.detail;
  }

  render() {
    if (this.isNewRoute === false) {
      return <pg-spinner />;
    }

    const useSCPAuth = this.settings?.enable_new_auth ?? false;

    return (
      <stencil-router>
        <stencil-route-switch>
          <stencil-route
            url="/registration/confirmation"
            component="accounts-page-registration-confirmation"
          />
          <stencil-route url="/facebook/authorize" component={`accounts-page-newlinkfacebook`} />
          <stencil-route
            url="/instagram/authenticate"
            component="accounts-page-instagram-authenticate"
          />
          <stencil-route url="/instagram/authorize" component="accounts-page-instagram-authorize" />
          {/* https://linear.app/planoly/issue/PLN-39/ : Need to be removed after Mobile catches up */}
          <stencil-route url="/tiktok/authenticate" component="accounts-page-tiktok-authenticate" />
          {/* <stencil-route url="/tiktok/authorize" component="accounts-page-tiktok-authorize" /> */}
          <stencil-route
            url="/pinterest/authenticate"
            component="accounts-page-pinterest-authenticate"
          />
          <stencil-route url="/pinterest/authorize" component="accounts-page-pinterest-authorize" />
          {this.settings.web_enable_scp_notifications_refactor ? (
            <AuthenticateRoute
              url="/notifications"
              component="accounts-notifications-comprehensive"
              useSCPAuth={useSCPAuth}
            />
          ) : (
            <AuthenticateRoute
              url="/notifications"
              component="accounts-notifications"
              useSCPAuth={useSCPAuth}
            />
          )}
          {/* https://linear.app/planoly/issue/PLN-159 : Need to be removed after Mobile catches up */}
          <AuthenticateRoute
            url="/link/twitter"
            component="accounts-page-twitter-authenticate"
            useSCPAuth={useSCPAuth}
          />
          <AuthenticateRoute
            url="/twitter/authenticate"
            component="accounts-page-twitter-authenticate"
            useSCPAuth={useSCPAuth}
          />
          <AuthenticateRoute
            url="/twitter/reauth"
            component="accounts-page-twitter-authenticate"
            useSCPAuth={useSCPAuth}
          />
          {/* https://linear.app/planoly/issue/PLN-159 : Need to be removed after Mobile catches up */}
          <AuthenticateRoute
            url="/twitter/authorize"
            component="accounts-page-twitter-authorize"
            useSCPAuth={useSCPAuth}
          />
          <AuthenticateRoute
            url="/link/instagram"
            component={`accounts-page-newinstagram`}
            useSCPAuth={useSCPAuth}
          />
          <AuthenticateRoute
            url="/link/twitter"
            component="accounts-page-twitter-authenticate"
            useSCPAuth={useSCPAuth}
          />
          {/* https://linear.app/planoly/issue/PLN-39/ : Need to be removed after Mobile catches up */}
          <AuthenticateRoute
            url="/link/tiktok"
            component="accounts-page-tiktok"
            useSCPAuth={useSCPAuth}
          />
          <AuthenticateRoute
            url="/link/pinterest"
            component="accounts-page-pinterest"
            useSCPAuth={useSCPAuth}
          />
          <AuthenticateRoute
            url="/onboarding"
            exact
            component={`accounts-page-newonboarding`}
            useSCPAuth={useSCPAuth}
          />
          <AuthenticateRoute
            url={[`/onboarding/success`, `/ig-authenticate/success`]}
            component="accounts-page-onboarding-success"
            useSCPAuth={useSCPAuth}
            exact
          />
          <AuthenticateRoute
            url="/ig-authenticate"
            component="accounts-page-link-instagram"
            useSCPAuth={useSCPAuth}
          />
          <AuthenticateRoute
            url="/link/ig"
            component="accounts-page-newlinkinstagram"
            useSCPAuth={useSCPAuth}
          />
          <AuthenticateRoute
            url="/link/fb"
            component="accounts-page-newlinkfacebook"
            useSCPAuth={useSCPAuth}
          />
          {/* New component to address all the platforms auth/reauth processes */}
          <AuthenticateRoute
            url={['/authorize/:platform', '/tiktok/authorize']}
            component="accounts-platform-authorize"
            useSCPAuth={useSCPAuth}
          />
          {/* TODO: love logic from linked in organizations into the generic one */}
          <AuthenticateRoute
            url="/linkedin/organizations/:platformAccountId"
            component="accounts-page-linkedin-organizations"
            useSCPAuth={useSCPAuth}
          />
          <AuthenticateRoute
            url="/list/organizations/:platform"
            component="accounts-page-list-organizations"
            useSCPAuth={useSCPAuth}
          />
          <AuthenticateRoute url="/" component="accounts-page-index" useSCPAuth={useSCPAuth} />
          {this.currentUser && !this.currentUser.plan.isFree() && (
            <accounts-pendo-analytics user={this.currentUser} />
          )}
        </stencil-route-switch>
      </stencil-router>
    );
  }
}
